/**
 * IntegrationDrawer — Add or Edit a vendor integration.
 *
 * Conditional fields based on vendor_auth_type_id:
 *   Type 1: External Campus ID + Credentials + Auth Token
 *   Type 2: External Campus ID + Auth Token (no credentials)
 *   Type 4: External Campus ID + Auth Token (no credentials)
 *
 * Receiving / Sending checkboxes respect force_feature and feature_receive/feature_send flags.
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   onSave        — (formData) => void
 *   onDelete      — (item) => void — delete integration (edit mode, inactive only)
 *   saving        — boolean
 *   mode          — 'add' | 'edit'
 *   vendorItem    — the vendor/integration record being added or edited
 */

import { useState, useEffect, useRef } from 'react';

/** Determine truthy from 1, true, or boolean */
const isTruthy = (val) => val === 1 || val === true;

const IntegrationDrawer = ({ open, onClose, onSave, onDelete, saving, mode = 'add', vendorItem }) => {
  const [externalCampusId, setExternalCampusId] = useState('');
  const [credentials, setCredentials] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isReceiving, setIsReceiving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errors, setErrors] = useState({});

  const firstInputRef = useRef(null);

  // Derive field visibility from auth type
  const authTypeId = vendorItem?.vendor_auth_type_id;
  const showExternalId = [1, 2, 4].includes(authTypeId);
  const showCredentials = authTypeId === 1;
  const showAuthToken = [1, 2, 4].includes(authTypeId);

  // Determine if feature checkboxes are forced
  const forceFeature = isTruthy(vendorItem?.force_feature);
  const hasReceiveFeature = isTruthy(vendorItem?.feature_receive);
  const hasSendFeature = isTruthy(vendorItem?.feature_send);

  // In edit mode, show receive/send based on the item's feature flags
  // In add mode, show based on the vendor's feature capabilities
  const showReceiving = mode === 'edit'
    ? hasReceiveFeature || isTruthy(vendorItem?.is_receiving)
    : hasReceiveFeature;
  const showSending = mode === 'edit'
    ? hasSendFeature || isTruthy(vendorItem?.is_sending)
    : hasSendFeature;

  // Show delete button only in edit mode when integration is inactive
  const showDelete = mode === 'edit' && !vendorItem?.is_active;

  // Reset form when drawer opens / item changes
  useEffect(() => {
    if (open && vendorItem) {
      if (mode === 'edit') {
        setExternalCampusId(vendorItem.external_campus_id || '');
        setCredentials(vendorItem.credentials || '');
        setAuthToken(vendorItem.auth || '');

        // For edit mode: respect force_feature
        if (forceFeature) {
          setIsReceiving(hasReceiveFeature);
          setIsSending(hasSendFeature);
        } else {
          setIsReceiving(isTruthy(vendorItem.is_receiving) || isTruthy(vendorItem.is_incomming));
          setIsSending(isTruthy(vendorItem.is_sending));
        }
      } else {
        // Add mode — clear fields
        setExternalCampusId('');
        setCredentials('');
        setAuthToken('');

        if (forceFeature) {
          setIsReceiving(hasReceiveFeature);
          setIsSending(hasSendFeature);
        } else {
          setIsReceiving(hasReceiveFeature);
          setIsSending(hasSendFeature);
        }
      }
      setErrors({});

      // Focus first input after drawer animation
      setTimeout(() => firstInputRef.current?.focus(), 150);
    }
  }, [open, vendorItem, mode, forceFeature, hasReceiveFeature, hasSendFeature]);

  // ── Validation ──────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (showExternalId && !externalCampusId.trim()) {
      errs.externalCampusId = 'External Campus ID is required.';
    }
    if (showCredentials && !credentials.trim()) {
      errs.credentials = 'Credentials are required.';
    }
    if (showAuthToken && !authToken.trim()) {
      errs.authToken = 'Auth Token is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Determine receiving/sending values
    let finalReceiving, finalSending;
    if (forceFeature) {
      finalReceiving = hasReceiveFeature;
      finalSending = hasSendFeature;
    } else {
      finalReceiving = isReceiving;
      finalSending = isSending;
    }

    onSave({
      externalCampusId: externalCampusId.trim(),
      credentials: credentials.trim(),
      authToken: authToken.trim(),
      isReceiving: finalReceiving,
      isSending: finalSending,
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label={mode === 'add' ? 'Add Integration' : 'Edit Integration'}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">{mode === 'add' ? 'Add Integration' : 'Edit Integration'}</h2>
        <button type="button" className="dp-drawer__close" onClick={onClose} aria-label="Close">{'\u2715'}</button>
      </div>

      {/* Vendor name subtitle */}
      {vendorItem?.vendor_name && (
        <div style={{ padding: '0 1.5rem 0.5rem', color: 'var(--color-text-secondary, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)', fontWeight: 'var(--font-weight-medium, 500)' }}>
          {vendorItem.vendor_name}
        </div>
      )}

      <form className="dp-drawer__body" onSubmit={handleSubmit} noValidate>
        {/* External Campus ID */}
        {showExternalId && (
          <div className="dp-drawer__field">
            <label htmlFor="int-ext-id" className="dp-drawer__label">External Campus ID *</label>
            <input
              ref={firstInputRef}
              id="int-ext-id"
              type="text"
              className={`dp-drawer__input${errors.externalCampusId ? ' is-invalid' : ''}`}
              value={externalCampusId}
              onChange={(e) => setExternalCampusId(e.target.value)}
              placeholder="Enter external campus ID"
              disabled={saving}
            />
            {errors.externalCampusId && <span className="dp-drawer__error">{errors.externalCampusId}</span>}
          </div>
        )}

        {/* Credentials (type 1 only) */}
        {showCredentials && (
          <div className="dp-drawer__field">
            <label htmlFor="int-credentials" className="dp-drawer__label">Credentials *</label>
            <input
              id="int-credentials"
              type="text"
              className={`dp-drawer__input${errors.credentials ? ' is-invalid' : ''}`}
              value={credentials}
              onChange={(e) => setCredentials(e.target.value)}
              placeholder="Enter credentials"
              disabled={saving}
            />
            {errors.credentials && <span className="dp-drawer__error">{errors.credentials}</span>}
          </div>
        )}

        {/* Auth Token */}
        {showAuthToken && (
          <div className="dp-drawer__field">
            <label htmlFor="int-auth-token" className="dp-drawer__label">Auth Token *</label>
            <input
              id="int-auth-token"
              type="text"
              className={`dp-drawer__input${errors.authToken ? ' is-invalid' : ''}`}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder="Enter auth token"
              disabled={saving}
            />
            {errors.authToken && <span className="dp-drawer__error">{errors.authToken}</span>}
          </div>
        )}

        {/* Feature checkboxes */}
        {(showReceiving || showSending) && (
          <div className="dp-drawer__field">
            <span className="dp-drawer__label">Features</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}>
              {showReceiving && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: forceFeature ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isReceiving}
                    onChange={(e) => setIsReceiving(e.target.checked)}
                    disabled={saving || forceFeature}
                  />
                  <span style={{ fontSize: 'var(--font-size-sm, 0.875rem)', color: 'var(--color-text-primary, #111827)' }}>
                    Receiving Data
                  </span>
                </label>
              )}
              {showSending && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: forceFeature ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isSending}
                    onChange={(e) => setIsSending(e.target.checked)}
                    disabled={saving || forceFeature}
                  />
                  <span style={{ fontSize: 'var(--font-size-sm, 0.875rem)', color: 'var(--color-text-primary, #111827)' }}>
                    Sending Data
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="dp-drawer__footer">
          {showDelete && (
            <button
              type="button"
              className="dp-btn dp-btn--danger"
              onClick={() => onDelete(vendorItem)}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              Delete
            </button>
          )}
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving...' : mode === 'add' ? 'Set Up' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntegrationDrawer;
